import { startOfMonth, endOfMonth, startOfWeek, endOfWeek } from 'date-fns';
import { Reservation } from './types';

export const RESERVATIONS_PAGE_SIZE = 100;
export const RESERVATIONS_MAX_PAGES = 20;

/** Monday-first visible month grid (includes adjacent-month days). */
export function getMonthGridRange(currentDate: Date): { rangeStart: Date; rangeEnd: Date } {
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const rangeStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  rangeStart.setHours(0, 0, 0, 0);
  const rangeEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  rangeEnd.setHours(23, 59, 59, 999);
  return { rangeStart, rangeEnd };
}

/** Monday–Sunday week containing currentDate. */
export function getWeekRangeBounds(currentDate: Date): { rangeStart: Date; rangeEnd: Date } {
  const weekStart = new Date(currentDate);
  const day = weekStart.getDay();
  const diff = weekStart.getDate() - day + (day === 0 ? -6 : 1);
  weekStart.setDate(diff);
  weekStart.setHours(0, 0, 0, 0);
  const rangeEnd = new Date(weekStart);
  rangeEnd.setDate(weekStart.getDate() + 6);
  rangeEnd.setHours(23, 59, 59, 999);
  return { rangeStart: weekStart, rangeEnd };
}

export interface FetchReservationsInRangeOptions {
  getApiUrl: (path: string) => string;
  token: string;
  rangeStart: Date;
  rangeEnd: Date;
  roomId?: string;
  signal?: AbortSignal;
  pageSize?: number;
  maxPages?: number;
}

/**
 * Paginate GET /reservations until a short page or maxPages.
 * Avoids the single-request 200-row cap hiding bookings.
 */
export async function fetchAllReservationsInRange(
  options: FetchReservationsInRangeOptions
): Promise<Reservation[]> {
  const {
    getApiUrl,
    token,
    rangeStart,
    rangeEnd,
    roomId,
    signal,
    pageSize = RESERVATIONS_PAGE_SIZE,
    maxPages = RESERVATIONS_MAX_PAGES,
  } = options;

  const all: Reservation[] = [];
  let offset = 0;

  for (let page = 0; page < maxPages; page += 1) {
    const params = new URLSearchParams({
      startDate: rangeStart.toISOString(),
      endDate: rangeEnd.toISOString(),
      limit: String(pageSize),
      offset: String(offset),
    });
    if (roomId) {
      params.set('roomId', roomId);
      params.set('status', 'confirmed');
    }

    const response = await fetch(getApiUrl(`/reservations?${params.toString()}`), {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const batch: Reservation[] = await response.json();
    if (!Array.isArray(batch)) {
      throw new Error('Invalid reservations response');
    }
    all.push(...batch);

    if (batch.length < pageSize) break;
    offset += pageSize;
  }

  return all;
}
